import { createRef, useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { membersAppSelectors } from 'redux/slices/memberSlice';
import { loadMemberNeeds, needsSelectors } from 'redux/slices/needs.slice';
import { selectSelectedMemberId } from 'redux/slices/selectedMemberSlice';
import { useAppDispatch, useAppSelector } from 'redux/store-hooks';
import NeedCard from './syva-need-card';
import { SyvaNeedsFilter } from './syva-needs-filters';
import syvaNeedsUtils from './syva-needs-utils';
import { useSearchParams } from 'react-router-dom';

interface NeedsListProps {
  filters?: SyvaNeedsFilter[];
}

const HIGHLIGHTED_CLASS = 'highlighted';

const NeedsList = ({ filters }: NeedsListProps) => {
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [initialRefs, setInitialRefs] = useState<{
    [key in string]: React.RefObject<HTMLDivElement>;
  }>(null);

  const selectedMemberId = useAppSelector(selectSelectedMemberId);
  const member = membersAppSelectors.useSelectMemberById(selectedMemberId);
  const needs = needsSelectors
    .useSelectNeedsByMember(selectedMemberId)
    .sort((a, b) => b.identifiedAt - a.identifiedAt);

  useEffect(() => {
    dispatch(loadMemberNeeds({ memberId: selectedMemberId }));
  }, [dispatch, selectedMemberId]);

  useEffect(() => {
    if (needs.length > 0) {
      const refs = {};
      needs.forEach(need => {
        refs[`need-${need.id}`] = createRef();
      });
      setInitialRefs(refs);
    }
  }, [needs]);

  useEffect(() => {
    function scrollToNeed(needId: string): void {
      const needRefId = `need-${needId}`;
      const ref = initialRefs[needRefId];
      ref?.current.scrollIntoView({
        behavior: 'smooth',
      });
      ref?.current.classList.add(HIGHLIGHTED_CLASS);

      // Remove highlighted class from other needs - so that we can re-highlight them again if you click the same need again
      Object.entries(initialRefs).forEach(([key, otherRef]) => {
        if (key !== needRefId) {
          otherRef?.current?.classList?.remove(HIGHLIGHTED_CLASS);
        }
      });
    }

    if (initialRefs) {
      if (searchParams.has('needId')) {
        const id = searchParams.get('needId');
        scrollToNeed(id);
      }
    }
  }, [initialRefs, searchParams]);

  return needs.length ? (
    <Box
      ref={scrollerRef}
      className="needs-list"
      sx={{
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        marginTop: '16px',
      }}
    >
      {[...needs]
        .filter(need => {
          if (!filters || !filters.length) {
            return true;
          }

          let needLabel = syvaNeedsUtils
            .calculateNeedLabel(need, member)
            .toLocaleLowerCase();

          if (needLabel === 'stopped pursuing') {
            needLabel = 'unresolved';
          }

          return filters.includes(needLabel as SyvaNeedsFilter);
        })
        .map(need => {
          const key = `need-${need.id}`;
          const ref =
            !!initialRefs && key in initialRefs ? initialRefs[key] : null;
          return <NeedCard key={key} ref={ref} need={need} />;
        })}
    </Box>
  ) : null;
};

export default NeedsList;
